<script lang="ts" setup>
import { ref, computed, watch } from 'vue'

import InfoIcon from '@lahaus-roomie/roomie/src/assets/icons/info.svg'
import RoomieRange from '@lahaus-roomie/roomie/src/components/RoomieRange/index.vue'
import RoomieTooltip from '@lahaus-roomie/roomie/src/components/RoomieTooltip/index.vue'

import type { Props } from './types'
import { calculateDownPayment, getMonthlyPayments } from '@/components/QuotationPlan/utils'

const MIN_DOWN_PAYMENT = 50_000
const DEFAULT_RANGE_STEP = 5000

const props = defineProps<Props>()

const { t, n } = useI18n()
const { $trackEvent, $useLhId } = useNuxtApp()

const calculateInitialDownPayment = () => {
  return calculateDownPayment(props.initialFeePrice, props.separationPrice)
}

const initialDownPayment = computed(() => calculateInitialDownPayment())

const maxDownPayment = computed(() => {
  return initialDownPayment.value + props.monthlyPaymentPrice
})

const initialPayment = ref<number>(initialDownPayment.value)

const getMonthlyPaymentPrice = () => props.monthlyPaymentPrice

const monthlyPaymentsCount = computed(() => {
  const monthlyPaymentPrice = getMonthlyPaymentPrice()
  return monthlyPaymentPrice ? getMonthlyPayments(props.monthlyPaymentsAndAdditions).length : 0
})

const calculateMonthlyPayment = (max: number, initial: number, count: number) =>
  count ? Math.round((max - initial) / count) : 0

const monthlyPayment = computed(() =>
  calculateMonthlyPayment(maxDownPayment.value, initialPayment.value, monthlyPaymentsCount.value)
)

const getCreditPrice = () => props.creditPrice

const lastPayment = computed(() => getCreditPrice())

const getPropertyPrice = () => props.propertyPrice

const totalPayment = computed(() => getPropertyPrice())

const rangeStep = ref<number>(DEFAULT_RANGE_STEP)

const hasMonthlyPayment = () => monthlyPayment.value > 0

const canShowMonthlyPayments = computed(() => hasMonthlyPayment())

const isDisclaimerVisible = ref(false)

const setInitialPayment = (value: number) => {
  initialPayment.value = value
}

const resetInitialPayment = () => {
  setInitialPayment(initialDownPayment.value)
}

const getPaymentPlanData = () => ({
  listings: [{
    id: props.listingId,
    unit_id: props.unitCode.slice(0, 32)
  }],
  initial_payment_value: initialPayment.value,
  monthly_payments_count: monthlyPaymentsCount.value,
  monthly_fee_value: monthlyPayment.value
})

const trackSelectedValue = () => {
  $trackEvent({
    name: 'payment_plan_simulated',
    version: '1-0-0',
    data: getPaymentPlanData()
  })
}

const onSimulatorPropsChange = () => {
  resetInitialPayment()
}

watch([
  () => props.initialFeePrice,
  () => props.separationPrice,
  () => props.monthlyPaymentPrice,
  () => props.creditPrice,
  () => props.propertyPrice,
  () => props.monthlyPaymentsAndAdditions
], onSimulatorPropsChange)

const getRoundingBounds = () => ({
  min: maxDownPayment.value - DEFAULT_RANGE_STEP,
  max: maxDownPayment.value
})

const shouldRoundToMax = (newInitialPayment: number, minLimit: number, maxLimit: number) =>
  rangeStep.value === DEFAULT_RANGE_STEP && newInitialPayment > minLimit && newInitialPayment <= maxLimit

const shouldResetRangeStep = (newInitialPayment: number, minLimit: number) =>
  newInitialPayment <= minLimit && rangeStep.value !== DEFAULT_RANGE_STEP

watch(initialPayment, (newInitialPayment) => {
  const { min, max } = getRoundingBounds()

  if (shouldRoundToMax(newInitialPayment, min, max)) {
    rangeStep.value = 1
    initialPayment.value = max
  } else if (shouldResetRangeStep(newInitialPayment, min)) {
    rangeStep.value = DEFAULT_RANGE_STEP
  }
})

</script>

<script lang="ts">
export default {
  name: 'QuotationPlanFeeSimulator'
}
</script>

<template>
  <div class="quotation-plan-fee-simulator">
    <div class="quotation-plan-fee-simulator-title text-20 flex">
      <span
        v-sanitize="t('range.title')"
        class="display-7 lg:display-6" />
    </div>

    <div class="flex flex-col lg:flex-row mt-24 lg:mt-40 justify-between gap-2 lg:gap-0">
      <span class="body-1-m lg:hl-3-sb text-pine-600">
        {{ t('table.reservationFee') }}
      </span>

      <div class="quotation-plan-fee-simulator-initial-payment font-semibold text-24 lg:hl-1-sb flex items-center gap-4">
        {{ n(initialPayment, 'currency') }}

        <span class="body-3-m">
          {{ currency }}
        </span>
      </div>
    </div>

    <div class="mt-8 lg:mt-4">
      <RoomieRange
        id="quotation-plan-fee-simulator-range"
        v-model="initialPayment"
        :tracking-lh-id="$useLhId({...dataAttributes.dataLh, A: 'seleccionar', C: `separacion_${initialPayment}` })"
        variant="brand"
        :step="rangeStep"
        :min="MIN_DOWN_PAYMENT"
        :max="maxDownPayment"
        @mouseup="trackSelectedValue"
        @touchend="trackSelectedValue" />
    </div>

    <div class="flex gap-12 mt-40 lg:mt-32">
      <ul class="flex flex-col justify-between w-full">
        <li class="quotation-plan-fee-simulator__list-item">
          <span class="quotation-plan-fee-simulator__list-item-monthly-payments body-1-m lg:hl-3-sb">
            <template v-if="canShowMonthlyPayments">
              {{ t('table.monthlyPayments.variable', { count: monthlyPaymentsNumber }) }}
            </template>

            <template v-else>
              {{ t('table.monthlyPayments.fixed') }}
            </template>
          </span>

          <div class="flex flex-row items-center gap-4">
            <span
              class="quotation-plan-fee-simulator__list-item-value-small"
              :class="{'font-regular': !canShowMonthlyPayments}">
              <template v-if="canShowMonthlyPayments">
                {{ n(monthlyPayment, 'currency') }}
              </template>

              <template v-else>
                {{ t('notApply') }}
              </template>
            </span>

            <span
              v-if="canShowMonthlyPayments"
              class="body-3-m text-pine-600">
              {{ currency }}
            </span>
          </div>
        </li>

        <hr class="divider my-16 lg:my-24">

        <li class="quotation-plan-fee-simulator__list-item">
          <span class="quotation-plan-fee-simulator__list-item-last-payment body-1-m lg:hl-3-sb">
            {{ t('table.lastPayment') }}
          </span>

          <div class="flex flex-row items-center gap-4">
            <span class="quotation-plan-fee-simulator__list-item-value">
              {{ n(lastPayment, 'currency') }}
            </span>

            <span class="body-3-m text-pine-600">
              {{ currency }}
            </span>
          </div>
        </li>

        <hr class="divider my-16 lg:my-24">

        <li class="quotation-plan-fee-simulator__list-item">
          <div class="flex flex-row">
            <span class="quotation-plan-fee-simulator__list-item-total-payment body-1-m lg:hl-3-sb">
              {{ t('table.totalPayment') }}
            </span>

            <div class="relative">
              <InfoIcon
                id="quotation-plan-fee-simulator-disclaimer-icon"
                class="fill-carbon-300 w-24 h-24 ml-8"
                :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'ver', C: 'informacion sobre el precio' })"
                @mouseover="isDisclaimerVisible = true"
                @click="isDisclaimerVisible = !isDisclaimerVisible" />

              <RoomieTooltip
                id="quotation-plan-fee-simulator-disclaimer-tooltip"
                class="absolute"
                variant="message"
                :is-visible="isDisclaimerVisible"
                indicator-placement="bottom left"
                @tooltip-closed="isDisclaimerVisible = false">
                <template #description>
                  {{ t('disclaimer') }}
                </template>
              </RoomieTooltip>
            </div>
          </div>

          <div class="flex flex-row items-center gap-4">
            <span class="quotation-plan-fee-simulator__list-item-value">
              {{ n(totalPayment, 'currency') }}
            </span>

            <span class="body-3-m  text-pine-600">
              {{ currency }}
            </span>
          </div>
        </li>
      </ul>
    </div>

    <slot />
  </div>
</template>

<i18n src="./i18n.json" lang="json" />

<style lang="scss">
.lh-roomie-v2 {
  .quotation-plan-fee-simulator {
    &__list-item {
      @apply flex justify-between items-center text-carbon-600 text-14 lg:text-16;

      &-value {
        @apply font-medium text-right text-carbon-600 text-20 lg:text-22 ml-10;
      }
    }

    #quotation-plan-fee-simulator-disclaimer-tooltip {
      @apply z-20;

      top: -11.5rem;
      left: -9rem;

      .roomie-tooltip__indicator {
        left: 10.3rem;
      }
    }

    .quotation-plan-fee-simulator-title {
      color: #004132;
    }

    .quotation-plan-fee-simulator-initial-payment {
      color: #004132;
    }

    .quotation-plan-fee-simulator__list-item-monthly-payments {
      color: #004132;
    }

    .quotation-plan-fee-simulator__list-item-last-payment {
      color: #004132;
    }

    .quotation-plan-fee-simulator__list-item-total-payment {
      color: #004132;
    }

    .divider {
      opacity: 0.2;
      background: #004132;
    }

    .quotation-plan-fee-simulator__list-item-value {
      color: #004132;
      text-align: right;
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      line-height: 120%;
      letter-spacing: -1.04px;

      @media (width >= 1024px) {
        @apply hl-2-sb;
      }
    }

    .quotation-plan-fee-simulator__list-item-value-small {
      @apply hl-3-sb;

      color: var(--color-pine-600);

      @media (width >= 1024px) {
        @apply hl-2-sb;
      }
    }

    #quotation-plan-fee-simulator-range {
      &::-webkit-slider-thumb {
        background-color: #2DE187;
        border-color: #2DE187;
        filter: none;
      }

      &::-moz-range-thumb {
        background-color: #2DE187;
        border-color: #2DE187;
        filter: none;
      }

      &::-webkit-slider-runnable-track {
        background:
          linear-gradient(
            to right,
            #2DE187,
            #2DE187 calc(var(--track-width) - 1px),
            #C2D0D0 var(--track-width)
          );
      }

      &::-moz-range-track {
        background:
          linear-gradient(
            to right,
            #2DE187,
            #2DE187 calc(var(--track-width) - 1px),
            #C2D0D0 var(--track-width)
          );
      }
    }
  }
}
</style>
